import { Badge, StrongText, Subheading, SurfaceCard, SurfaceRow } from '../../../components/ui'
import type { ComponentProps } from 'react'
import { FAMILY_MEMBERS, FAMILY_HISTORY } from '../constants'
import { uiClasses } from '../../shared/uiClasses'

const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint
type BadgeColor = ComponentProps<typeof Badge>['variant']

const CATEGORY_COLORS: Record<
  'cardiovascular' | 'oncology' | 'metabolic' | 'endocrine',
  { badge: BadgeColor }
> = {
  cardiovascular: {
    badge: 'rose',
  },
  oncology: {
    badge: 'amber',
  },
  metabolic: {
    badge: 'violet',
  },
  endocrine: {
    badge: 'sky',
  },
}
type FamilyConditionCategory = keyof typeof CATEGORY_COLORS

function getCategory(condition: string): FamilyConditionCategory | null {
  const lower = condition.toLowerCase()
  if (lower.includes('heart') || lower.includes('hypertension') || lower.includes('cardiac') || lower.includes('mi'))
    return 'cardiovascular'
  if (lower.includes('cancer') || lower.includes('tumor') || lower.includes('carcinoma'))
    return 'oncology'
  if (lower.includes('diabetes') || lower.includes('metabolic') || lower.includes('cholesterol'))
    return 'metabolic'
  if (lower.includes('thyroid') || lower.includes('hashimoto') || lower.includes('endocrine'))
    return 'endocrine'
  return null
}

export default function FamilyTab() {
  return (
    <div className="space-y-6">
      {/* ── Family Members ──────────────────────────────────── */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Family Members</Subheading>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {FAMILY_MEMBERS.map((member) => (
            <SurfaceRow key={member.id}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <StrongText>{member.name}</StrongText>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Badge variant="zinc">
                      {member.relationship}
                    </Badge>
                    <span className={`text-xs/5 ${TEXT_SUBTLE}`}>Age {member.age}</span>
                  </div>
                </div>

                {/* Shared access indicator */}
                {member.sharedAccess && (
                  <span
                    className={`mt-1 flex items-center gap-1 text-xs/5 ${TEXT_SUBTLE}`}
                    title="Shared access enabled"
                  >
                    <span className={`inline-block h-2 w-2 rounded-full ${uiClasses.dotMuted}`} />
                    Shared
                  </span>
                )}
              </div>

              {/* Conditions */}
              <div className="mt-3 space-y-1">
                {member.conditions.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {member.conditions.map((c) => (
                      <Badge key={c} variant="yellow">
                        {c}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className={`text-xs/5 ${TEXT_FAINT}`}>No known conditions</p>
                )}
              </div>

              {/* Medications & last checkup */}
              <div className={`mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs/5 ${TEXT_SUBTLE}`}>
                <span>
                  {member.medications} medication{member.medications !== 1 ? 's' : ''}
                </span>
                <span>Last checkup: {member.lastCheckup}</span>
              </div>
            </SurfaceRow>
          ))}
        </div>
      </SurfaceCard>

      {/* ── Family Medical History ──────────────────────────── */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Family Medical History</Subheading>

        <div className="mt-4 space-y-3">
          {FAMILY_HISTORY.map((entry) => {
            const category = getCategory(entry.condition)
            const colors = category ? CATEGORY_COLORS[category] : null

            return (
              <SurfaceRow key={entry.id}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <StrongText>
                        {entry.condition}
                      </StrongText>
                      {category && colors && (
                        <Badge variant={colors.badge}>
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </Badge>
                      )}
                    </div>
                    <p className={`mt-0.5 text-sm/6 ${TEXT_SECONDARY}`}>
                      {entry.relative} · Diagnosed at age {entry.age}
                    </p>
                    {entry.notes && (
                      <p className={`mt-1 text-xs/5 italic ${TEXT_SUBTLE}`}>{entry.notes}</p>
                    )}
                  </div>
                </div>
              </SurfaceRow>
            )
          })}
        </div>
      </SurfaceCard>

      {/* ── Risk-score note ─────────────────────────────────── */}
      <SurfaceCard as="article">
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${uiClasses.iconSurface}`}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clipRule="evenodd"
              />
            </svg>
          </span>
          <div>
            <StrongText>
              How family history affects your risk scores
            </StrongText>
            <p className={`mt-1 text-sm/6 ${TEXT_SECONDARY}`}>
              First-degree relatives (parents, siblings, children) with cardiovascular disease,
              cancer, or metabolic disorders can elevate your personal risk scores by 15-40%
              depending on the condition, age of onset, and number of affected relatives. Our AI
              engine continuously cross-references your family medical history with the latest
              genomic and epidemiological research to refine your personalized risk
              assessments. Earlier age of onset in a relative generally indicates higher
              hereditary risk.
            </p>
          </div>
        </div>
      </SurfaceCard>
    </div>
  )
}
